import { apiClient } from '@/app/network/apiClient';
import type { User } from '@/user/domain/user';
import { userFromJson } from '@/user/domain/user';

import type { DeathCauseApi } from '../domain/deathCause';

export interface GameRun {
  id: string;
  score: number;
  durationMs: number;
  jumpCount: number;
  duckCount: number;
  deathCause: DeathCauseApi;
  wasPersonalBest: boolean;
  platform: string;
  playedAt: string;
}

export interface SubmitGameRunRequest {
  score: number;
  durationMs: number;
  jumpCount: number;
  duckCount: number;
  deathCause: DeathCauseApi;
  wasPersonalBest: boolean;
  platform: 'web';
}

function gameRunFromJson(json: Record<string, unknown>): GameRun {
  return {
    id: String(json.id ?? ''),
    score: Number(json.score ?? 0),
    durationMs: Number(json.duration_ms ?? 0),
    jumpCount: Number(json.jump_count ?? 0),
    duckCount: Number(json.duck_count ?? 0),
    deathCause: String(json.death_cause ?? '') as DeathCauseApi,
    wasPersonalBest: Boolean(json.was_personal_best),
    platform: String(json.platform ?? ''),
    playedAt: String(json.played_at ?? ''),
  };
}

export async function submitGameRun(
  request: SubmitGameRunRequest,
): Promise<{ run: GameRun; user: User; gamesPlayed: number }> {
  const body = await apiClient.post<Record<string, unknown>>('/v1/users/me/runs', {
    score: request.score,
    duration_ms: request.durationMs,
    jump_count: request.jumpCount,
    duck_count: request.duckCount,
    death_cause: request.deathCause,
    was_personal_best: request.wasPersonalBest,
    platform: request.platform,
  });
  return {
    run: gameRunFromJson(body.run as Record<string, unknown>),
    user: userFromJson(body.user as Record<string, unknown>),
    gamesPlayed: Number(body.games_played ?? 0),
  };
}

export async function listGameRuns(limit = 20, offset = 0): Promise<{
  runs: GameRun[];
  gamesPlayed: number;
  averageScore: number;
  mostFearedEnemy: string;
}> {
  const body = await apiClient.get<Record<string, unknown>>(
    `/v1/users/me/runs?limit=${limit}&offset=${offset}`,
  );
  const raw = body.runs;
  const runs = Array.isArray(raw)
    ? raw.filter((row): row is Record<string, unknown> => typeof row === 'object' && row !== null).map(gameRunFromJson)
    : [];
  return {
    runs,
    gamesPlayed: Number(body.games_played ?? 0),
    averageScore: Number(body.average_score ?? 0),
    mostFearedEnemy: String(body.most_feared_enemy ?? ''),
  };
}
